import React, { useState, useEffect, useContext } from "react";
import { mdiInformationOutline, mdiLayers, mdiTapeMeasure } from "@mdi/js";
import styled from "styled-components";
import intl from "react-intl-universal";
import { useLocation } from "wouter";

import { Plot } from "../../../../script/plot";
import { Button } from "../../../components/Button";
import { Tooltip } from "../../../components/Tooltip";
import { MainLayer } from "./layers/main/MainLayer";
import { MeasurementLayer } from "./layers/measurement/MeasurementLayer";
import { IxusSiteInfoLayer } from "./layers/siteInfo/IxusSiteInfoLayer";
import { EntryContext } from "../../../contexts/EntryContext";

const TabStyle = styled.div`
    padding: 20px 0 0 0;
    border-radius: 0px 0px 5px 5px;
    border-top: 3px solid ${props => props.theme.greyLight};
`;

const ContentStyle = styled.div`
    gap: 10px;
    display: flex;
    overflow: hidden;
    height: calc(100vh - 245px);
`;

const LeftStyle = styled.div`
    display: flex;
    width: 100%;
    min-width: 500px;
    overflow: hidden;
    justify-content: center;
`;

const MenuStyle = styled.div`
    position: absolute;
    z-index: 10;
    display: flex;
    gap: 6px;
    padding: 0 8px 6px 8px;
    border: 1px solid ${props => props.theme.greyLight};
    border-top: none;
    border-radius: 0 0 2px 2px;
    background-color: ${props => props.theme.greyLightest};
`;

const CanvasStyle = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
    #ixus-canvas-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden;
        border-radius: 2px;
        background-color: ${props => props.theme.white};
        border: 1px solid ${props => props.theme.greyLight};
    }
`;

const RightStyle = styled.div`
    min-width: 430px;
    max-width: 430px;
    overflow: hidden;
    padding: 0 1px 0 1px;
    >div {
        height: 100%;
        flex-direction: column;
    }
`;

export const IxusTab = () => {

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;

    const layers = Object.freeze({
        MAIN: "main",
        MEASUREMENT: "measurement",
        SITE_INFO: "siteInfo",
    });

    const [, setLocation] = useLocation();
    const [plot, setPlot] = useState();
    const [layer, setLayer] = useState(layers.MAIN);

    useEffect(() => {
        const p = new Plot("ixus-canvas", entry.id);
        setPlot(p);
    }, []);

    useEffect(() => {
        if (entry.id && plot) {
            plot.id = entry.id;
        }
    }, [entry.id]);

    const onChangeLayer = newLayer => {
        setLocation("/entry/" + entry.id + "/ixus/" + newLayer);
        setLayer(newLayer);
    };

    useEffect(() => {
        const basePath = `/entry/${entry.id}/ixus`;
        if (location.pathname?.startsWith(basePath)) {
            if (location.pathname === basePath) {
                setLocation(`${basePath}/${layer}`);
            }
        }
    }, [location.pathname, entry.id, layer]);

    const [selectedIndex, setSelectedIndex] = useState();

    return (
        <TabStyle>
            <ContentStyle>
                <LeftStyle>
                    {plot &&
                        <MenuStyle>
                            <Tooltip message={intl.get("entry.ixus.main.title")}>
                                <Button
                                    svgIcon={mdiLayers}
                                    disabled={layer === layers.MAIN}
                                    onClick={() => onChangeLayer(layers.MAIN)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.ixus.measurement.title")}>
                                <Button
                                    svgIcon={mdiTapeMeasure}
                                    disabled={layer === layers.MEASUREMENT || selectedIndex === undefined}
                                    onClick={() => onChangeLayer(layers.MEASUREMENT)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.ixus.siteInfo.title")}>
                                <Button
                                    svgIcon={mdiInformationOutline}
                                    disabled={layer === layers.SITE_INFO}
                                    onClick={() => onChangeLayer(layers.SITE_INFO)} />
                            </Tooltip>
                        </MenuStyle>
                    }
                    <CanvasStyle id="canvas-style">
                        <div id="ixus-canvas-wrapper">
                            <canvas id="ixus-canvas" />
                        </div>
                    </CanvasStyle>
                </LeftStyle>
                <RightStyle>
                    {plot &&
                        <>
                            <MainLayer 
                                plot={plot} 
                                show={layer === layers.MAIN} 
                                selectedIndex={selectedIndex}
                                setSelectedIndex={setSelectedIndex} />
                            <MeasurementLayer
                                plot={plot}
                                show={layer === layers.MEASUREMENT}
                                selectedIndex={selectedIndex} />
                            <IxusSiteInfoLayer
                                plot={plot}
                                show={layer === layers.SITE_INFO}
                                selectedIndex={selectedIndex} />
                        </>
                    }
                </RightStyle>
            </ContentStyle>
        </TabStyle>
    );
};
